// Where a room tool is aimed (docs/claude/rendering.md, *Plan tools in 3D*): ONE
// value every room tool's hook takes — its preview, click, press, a drag's move
// and its end — built by two adapters, the plan's from its viewport and mouse
// event (`planAim`), the 3D view's from where the pointer lands (ui/tools3d.ts
// `aim3dOf`). A tool's reaches and pixel tolerances read it, never the plan's
// zoom: in 3D a pixel is what it is on that screen where the pointer is. DOM-free.

type Vec2 = { x: number; y: number };

/** What the pointer is on, in 3D (a SecSrc-like record: its object and the
 *  feature of it), which decides what a plan point alone cannot. */
export interface AimOn { id: string; part?: { kind: string; id: string } }

/** A room tool's aim: the plan point (whole mm where the 3D view hands it over),
 *  its height where it matters (3D: where the pointer landed), how many mm one
 *  screen px is there, what the pointer is on (3D; null on the plan), Alt, and
 *  the plan's screen point (null in 3D: no plan grip is under a 3D pointer). */
export interface ToolAim {
  world: Vec2;
  z: number | null;
  mmPerPx: number;
  on: AimOn | null;
  alt: boolean;
  sp: Vec2 | null;
}

/** The plan's aim: its viewport's scale (px per mm) at every point. */
export function planAim(world: Vec2, scale: number, sp: Vec2 | null, alt = false): ToolAim {
  return { world, z: null, mmPerPx: 1 / (scale > 0 ? scale : 1), on: null, alt, sp };
}

/** The 3D view's aim: the point it landed on, its px per mm there (0 when the
 *  view cannot tell: one px is then a mm), what it is on. */
export function landedAim(point: { x: number; y: number; z: number }, pxPerMm: number, on: AimOn | null, alt = false): ToolAim {
  return { world: { x: point.x, y: point.y }, z: point.z, mmPerPx: pxPerMm > 0 ? 1 / pxPerMm : 1, on, alt, sp: null };
}

/** `px` screen pixels, in mm, where the tool is aimed: a pixel tolerance. */
export const pxAt = (aim: ToolAim, px: number): number => px * aim.mmPerPx;

/** How far from a room's wall the Bench, Inner-wall and Heater-wall tools still
 *  find it (mm): 250 px on screen, and 200 mm more. */
export const wallReach = (aim: ToolAim): number => pxAt(aim, 250) + 200;
/** The Glass, Opening and Door tools' reach: 250 px, and 150 mm more. */
export const paneReach = (aim: ToolAim): number => pxAt(aim, 250) + 150;

/** The bench the 3D pointer is on (its id), else null. */
export const aimedBench = (aim: ToolAim): string | null => (aim.on?.part?.kind === 'bench' ? aim.on.part.id : null);
/** The fixture (a backrest, a light) the 3D pointer is on (its id), else null. */
export const aimedFixture = (aim: ToolAim): string | null => (aim.on?.part?.kind === 'fixture' ? aim.on.part.id : null);
